/**
 * Selector of a part, supplier, booking, person or company by NAME (batch 8 T14c, #3036).
 *
 * The user never types or sees an id: options come from the existing authorised lists, one page at a time, searched
 * on the server where the list supports it, with a "show more" row for the next page. Nothing loads the whole
 * catalog to find one part. A chosen option keeps the label it was chosen by.
 */
import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/useDebounce';
import { useReportLookup, type LookupName, type LookupOption } from '@/hooks/useInventoryReports';
import type { Picked } from './reportFilters';

const MORE_ID = '\u0000more';

interface BaseProps {
  name: LookupName;
  label: string;
  helperText?: string | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
}

function useOptions(name: LookupName, input: string, selected: readonly Picked[]) {
  const { t } = useTranslation('inventoryReports');
  // The booking list cannot be searched; typing there would only refetch the same pages.
  const q = useDebounce(name === 'booking' ? '' : input, 300);
  const lookup = useReportLookup(name, q);
  const loaded: LookupOption[] = [...lookup.options];
  for (const s of selected) if (!loaded.some((o) => o.id === s.id)) loaded.unshift(s);
  const options: LookupOption[] = lookup.hasMore ? [...loaded, { id: MORE_ID, label: t('lookup.more') }] : loaded;
  return { lookup, options, t };
}

export function SingleLookup({ value, onChange, ...props }: BaseProps & { value: Picked | null; onChange: (value: Picked | null) => void }) {
  const [input, setInput] = useState('');
  const { lookup, options, t } = useOptions(props.name, input, value === null ? [] : [value]);
  return (
    <Autocomplete<LookupOption, false, false, false>
      size="small"
      options={options}
      value={value}
      disabled={props.disabled ?? false}
      filterOptions={(all) => all}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      inputValue={input}
      onInputChange={(_event, next, reason) => { if (reason !== 'reset') setInput(next); }}
      onChange={(_event, next) => {
        if (next?.id === MORE_ID) {
          lookup.fetchMore();
          return;
        }
        onChange(next === null ? null : { id: next.id, label: next.label });
      }}
      loading={lookup.isFetching}
      loadingText={t('state.loading')}
      noOptionsText={lookup.isError ? t('lookup.failed') : t('lookup.none')}
      clearText={t('lookup.clear')}
      openText={t('lookup.open')}
      closeText={t('lookup.close')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          error={props.error !== undefined}
          helperText={props.error ?? props.helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {lookup.isFetching ? <CircularProgress size={16} aria-label={t('state.loading')} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export function MultiLookup({
  value,
  onChange,
  max,
  ...props
}: BaseProps & { value: Picked[]; onChange: (value: Picked[]) => void; max: number }) {
  const [input, setInput] = useState('');
  const { lookup, options, t } = useOptions(props.name, input, value);
  return (
    <Autocomplete<LookupOption, true, false, false>
      multiple
      size="small"
      options={options}
      value={value}
      disabled={props.disabled ?? false}
      filterSelectedOptions
      filterOptions={(all) => all}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      getOptionDisabled={(o) => o.id !== MORE_ID && value.length >= max}
      inputValue={input}
      onInputChange={(_event, next, reason) => { if (reason !== 'reset') setInput(next); }}
      onChange={(_event, next) => {
        if (next.some((o) => o.id === MORE_ID)) {
          lookup.fetchMore();
          return;
        }
        onChange(next.map((o) => ({ id: o.id, label: o.label })));
      }}
      loading={lookup.isFetching}
      loadingText={t('state.loading')}
      noOptionsText={lookup.isError ? t('lookup.failed') : t('lookup.none')}
      clearText={t('lookup.clear')}
      openText={t('lookup.open')}
      closeText={t('lookup.close')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          error={props.error !== undefined}
          helperText={props.error ?? props.helperText ?? t('lookup.maxParts', { count: max })}
        />
      )}
    />
  );
}
